import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  Target, 
  Flame, 
  Sun, 
  Snowflake, 
  Clock, 
  Mail, 
  TrendingUp,
  Download,
  Filter
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Lead {
  id: string;
  email: string;
  name: string;
  company?: string;
  campaignName: string;
  score: number;
  temperature: 'hot' | 'warm' | 'cold';
  opened: number;
  replied: number;
  clicked: number;
  lastEngagement: Date;
  responseTime?: number; // hours
  sentiment?: 'positive' | 'neutral' | 'negative';
  intent?: string;
}

const LeadIntelligenceDashboard: React.FC = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [temperatureFilter, setTemperatureFilter] = useState<'all' | 'hot' | 'warm' | 'cold'>('all');
  const [sortBy, setSortBy] = useState<'score' | 'engagement' | 'recent'>('score');

  useEffect(() => {
    loadLeads();
  }, [user]);

  useEffect(() => {
    filterAndSortLeads();
  }, [leads, temperatureFilter, sortBy]);

  const loadLeads = async () => {
    setIsLoading(true);
    try {
      // In production, this would fetch from API
      // For now, simulating lead data
      const mockLeads: Lead[] = [
        {
          id: '1',
          email: 'john@techcorp.com',
          name: 'John Smith',
          company: 'TechCorp',
          campaignName: 'SaaS Demo Campaign',
          score: 95,
          temperature: 'hot',
          opened: 5,
          replied: 2,
          clicked: 3,
          lastEngagement: new Date(Date.now() - 2 * 3600000),
          responseTime: 1.5,
          sentiment: 'positive',
          intent: 'meeting_request'
        },
        {
          id: '2',
          email: 'sarah@startup.io',
          name: 'Sarah Johnson',
          company: 'Startup.io',
          campaignName: 'Agency Outreach',
          score: 75,
          temperature: 'warm',
          opened: 3,
          replied: 1,
          clicked: 2,
          lastEngagement: new Date(Date.now() - 24 * 3600000),
          responseTime: 12,
          sentiment: 'neutral',
          intent: 'interested'
        },
        {
          id: '3',
          email: 'mike@enterprise.com',
          name: 'Mike Brown',
          company: 'Enterprise Co',
          campaignName: 'Partnership Proposal',
          score: 35,
          temperature: 'cold',
          opened: 1,
          replied: 0,
          clicked: 0,
          lastEngagement: new Date(Date.now() - 7 * 24 * 3600000),
          sentiment: 'negative',
          intent: 'not_interested'
        },
      ];
      setLeads(mockLeads);
    } catch (error) {
      console.error('Error loading leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setIsLoading(false);
    }
  };

  const filterAndSortLeads = () => {
    let filtered = [...leads];

    // Filter by temperature
    if (temperatureFilter !== 'all') {
      filtered = filtered.filter(lead => lead.temperature === temperatureFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return b.score - a.score;
        case 'engagement':
          return (b.opened + b.replied * 5 + b.clicked * 2) - (a.opened + a.replied * 5 + a.clicked * 2);
        case 'recent':
          return b.lastEngagement.getTime() - a.lastEngagement.getTime();
        default:
          return 0;
      }
    });

    setFilteredLeads(filtered);
  };

  const calculateLeadScore = (lead: Lead): number => {
    let score = 0;
    
    // Engagement scoring
    score += lead.opened * 5;  // 5 points per open
    score += lead.replied * 20; // 20 points per reply
    score += lead.clicked * 10; // 10 points per click

    // Recency boost
    const hoursSinceEngagement = (Date.now() - lead.lastEngagement.getTime()) / (1000 * 3600);
    if (hoursSinceEngagement < 24) score += 20;
    else if (hoursSinceEngagement < 72) score += 10;

    // Response time (faster = higher score)
    if (lead.responseTime && lead.responseTime < 2) score += 15;
    else if (lead.responseTime && lead.responseTime < 6) score += 10;

    // Sentiment
    if (lead.sentiment === 'positive') score += 15;
    else if (lead.sentiment === 'neutral') score += 5;

    // Intent
    if (lead.intent === 'meeting_request') score += 20;
    else if (lead.intent === 'interested') score += 10;

    return Math.min(score, 100);
  };

  const getTemperatureIcon = (temp: string) => {
    switch (temp) {
      case 'hot':
        return <Flame className="w-5 h-5 text-red-600" />;
      case 'warm':
        return <Sun className="w-5 h-5 text-orange-600" />;
      case 'cold':
        return <Snowflake className="w-5 h-5 text-blue-600" />;
      default:
        return null;
    }
  };

  const getTemperatureBadge = (temp: string) => {
    const colors = {
      hot: 'bg-red-100 text-red-800',
      warm: 'bg-orange-100 text-orange-800',
      cold: 'bg-blue-100 text-blue-800'
    };
    return colors[temp as keyof typeof colors];
  };

  const exportToCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Name', 'Email', 'Company', 'Campaign', 'Score', 'Temperature', 'Opened', 'Replied', 'Clicked', 'Last Engagement'];
    const rows = filteredLeads.map(lead => [
      lead.name,
      lead.email,
      lead.company || '',
      lead.campaignName,
      lead.score,
      lead.temperature,
      lead.opened,
      lead.replied,
      lead.clicked,
      lead.lastEngagement.toISOString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lead-intelligence-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toast.success('Exported to CSV!');
  };

  const hotLeads = leads.filter(l => l.temperature === 'hot').length;
  const warmLeads = leads.filter(l => l.temperature === 'warm').length;
  const coldLeads = leads.filter(l => l.temperature === 'cold').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Lead Intelligence</h2>
          <p className="text-gray-600 mt-1">AI-powered lead scoring and prioritization</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Flame className="w-8 h-8 text-red-600" />
            <span className="text-3xl font-bold text-red-600">{hotLeads}</span>
          </div>
          <h3 className="font-semibold text-gray-900">🔥 Hot Leads</h3>
          <p className="text-sm text-gray-600 mt-1">Score 80-100 • High priority</p>
          <div className="mt-3 flex items-center text-sm text-red-600">
            <Target className="w-4 h-4 mr-1" />
            Follow up within 24h
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Sun className="w-8 h-8 text-orange-600" />
            <span className="text-3xl font-bold text-orange-600">{warmLeads}</span>
          </div>
          <h3 className="font-semibold text-gray-900">🌤️ Warm Leads</h3>
          <p className="text-sm text-gray-600 mt-1">Score 50-79 • Medium priority</p>
          <div className="mt-3 flex items-center text-sm text-orange-600">
            <Mail className="w-4 h-4 mr-1" />
            Nurture sequence
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Snowflake className="w-8 h-8 text-blue-600" />
            <span className="text-3xl font-bold text-blue-600">{coldLeads}</span>
          </div>
          <h3 className="font-semibold text-gray-900">❄️ Cold Leads</h3>
          <p className="text-sm text-gray-600 mt-1">Score 0-49 • Low priority</p>
          <div className="mt-3 flex items-center text-sm text-blue-600">
            <Clock className="w-4 h-4 mr-1" />
            Re-engagement campaign
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={temperatureFilter}
            onChange={(e) => setTemperatureFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Temperatures</option>
            <option value="hot">Hot Leads</option>
            <option value="warm">Warm Leads</option>
            <option value="cold">Cold Leads</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-500" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="score">Sort by Score</option>
            <option value="engagement">Sort by Engagement</option>
            <option value="recent">Sort by Recent Activity</option>
          </select>
        </div>

        <div className="ml-auto text-sm text-gray-600">
          Showing {filteredLeads.length} of {leads.length} leads
        </div>
      </div>

      {/* Leads Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temperature</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No leads found matching your filters
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                      <div className="text-sm text-gray-500">{lead.email}</div>
                      {lead.company && (
                        <div className="text-xs text-gray-400 mt-1">{lead.company}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {lead.campaignName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className={`h-2 rounded-full ${
                            lead.score >= 80 ? 'bg-red-600' :
                            lead.score >= 50 ? 'bg-orange-600' :
                            'bg-blue-600'
                          }`}
                          style={{ width: `${lead.score}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{lead.score}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full ${getTemperatureBadge(lead.temperature)}`}>
                      {getTemperatureIcon(lead.temperature)}
                      {lead.temperature.charAt(0).toUpperCase() + lead.temperature.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-3">
                      <span title="Opens">{lead.opened} 👀</span>
                      <span title="Replies">{lead.replied} 💬</span>
                      <span title="Clicks">{lead.clicked} 🖱️</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(() => {
                      const hours = Math.floor((Date.now() - lead.lastEngagement.getTime()) / (1000 * 3600));
                      if (hours < 1) return 'Just now';
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      return `${days}d ago`;
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button className="text-blue-600 hover:text-blue-900 font-medium">
                      View Details →
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Scoring Methodology */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">📊 Lead Scoring Methodology</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
          <div>• Opens: +5 points each</div>
          <div>• Replies: +20 points each</div>
          <div>• Clicks: +10 points each</div>
          <div>• Engaged within 24h: +20 points</div>
          <div>• Fast response (&lt;2h): +15 points</div>
          <div>• Positive sentiment: +15 points</div>
          <div>• Meeting request intent: +20 points</div>
          <div>• Max score: 100 points</div>
        </div>
      </div>
    </div>
  );
};

export default LeadIntelligenceDashboard;
